import React from 'react';
import {
  ApiError,
  authStatus,
  createRealtimeCredential,
  createSession,
  fetchContent,
  finishSession,
  login,
  logout,
  saveSessionTranscript,
} from './api';
import { unlockAudio } from './audio';
import { Brand, TopNav, Transcript, type Section } from './components';
import { PrepLibrary } from './PrepLibrary';
import { addPending, removePending } from './pending';
import { ContentContext, normalizeContent, type ContentBundle } from './content';
import { isInAppBrowser, IN_APP_MESSAGE } from './inapp';
import { Debrief, toTranscriptLines } from './Debrief';
import { ArrowIcon, LockIcon, SparkIcon } from './icons';
import { Lobby } from './Lobby';
import { PrepPack } from './PrepPack';
import { Sessions } from './Sessions';
import {
  END_ROLEPLAY_MESSAGE,
  START_ROLEPLAY_MESSAGE,
  phaseMinutes,
  tagTranscriptLines,
  type RolePlayPhase,
} from './roleplay';
import type { RealtimeRoom } from './realtime';
import { InterviewRoom } from './Room';
import { NEARLY_UP_MESSAGE, ROLE_LABELS, TIME_UP_MESSAGE } from './scenarios';
import { loadSetup, saveSetup } from './storage';
import type { InterviewSetup, SessionTranscriptBody, TranscriptEntry } from './types';

export { Transcript };

type Phase = 'checking' | 'locked' | 'lobby' | 'connecting' | 'active' | 'ending' | 'ended';

function formatError(error: unknown): string {
  if (error instanceof ApiError || error instanceof Error) return error.message;
  return 'Something went wrong. Try again.';
}

function useElapsedTime(startedAt: number | null, running: boolean): number {
  const [elapsed, setElapsed] = React.useState(0);
  React.useEffect(() => {
    if (!startedAt) {
      setElapsed(0);
      return undefined;
    }
    const update = () => setElapsed(Date.now() - startedAt);
    update();
    if (!running) return undefined;
    const timer = window.setInterval(update, 1_000);
    return () => window.clearInterval(timer);
  }, [running, startedAt]);
  return elapsed;
}

function AccessGate({
  error,
  loading,
  onSubmit,
}: {
  error: string;
  loading: boolean;
  onSubmit: (accessCode: string) => Promise<void>;
}) {
  const [accessCode, setAccessCode] = React.useState('');

  return (
    <main className="gate-page">
      <div className="gate-grain" />
      <header className="gate-header">
        <Brand />
        <span className="private-pill"><LockIcon /> Private</span>
      </header>
      <section className="gate-layout">
        <div className="gate-copy">
          <p className="eyebrow">Private practice room</p>
          <h1>Walk into the call<br />already warmed up.</h1>
          <p className="gate-lede">
            Live voice role plays and interviews that listen, push back,
            and finish with a written debrief.
          </p>
        </div>
        <form
          className="access-card"
          onSubmit={(event) => {
            event.preventDefault();
            void onSubmit(accessCode);
          }}
        >
          <span className="access-icon"><LockIcon /></span>
          <p className="eyebrow">Private practice room</p>
          <h2>Enter access code</h2>
          <p>This room is not public.</p>
          <label htmlFor="access-code">Access code</label>
          <input
            autoComplete="current-password"
            autoFocus
            id="access-code"
            onChange={(event) => setAccessCode(event.target.value)}
            placeholder="••••••••••••"
            type="password"
            value={accessCode}
          />
          {error && <div className="form-error" role="alert">{error}</div>}
          <button className="primary-button" disabled={loading || !accessCode.trim()} type="submit">
            <span>{loading ? 'Checking…' : 'Enter the studio'}</span>
            {!loading && <ArrowIcon />}
          </button>
          <small>Protected at the edge. Your microphone starts only after you press Start.</small>
        </form>
      </section>
      <footer className="gate-footer">
        <span>Private practice room</span>
        <span>Your microphone starts only after you press Start.</span>
      </footer>
    </main>
  );
}

function Connecting({ setup }: { setup: InterviewSetup }) {
    return (
    <main className="connecting-page">
      <Brand compact />
      <div className="connecting-stage">
        <div className="connecting-orbit">
          <span /><span /><span />
          <SparkIcon />
        </div>
        <p className="eyebrow">Opening the room</p>
        <h1>{ROLE_LABELS[setup.scenario].aiName} is getting ready.</h1>
        <p>Requesting microphone access and connecting a private WebRTC session…</p>
        <ol>
          <li className="is-complete">Room protected</li>
          <li className="is-active">Connecting voice</li>
          <li>Beginning the session</li>
        </ol>
      </div>
    </main>
  );
}

export default function App() {
  const [phase, setPhase] = React.useState<Phase>('checking');
  const [setup, setSetup] = React.useState<InterviewSetup>(loadSetup);
  const [error, setError] = React.useState('');
  const [loginLoading, setLoginLoading] = React.useState(false);
  const [transcript, setTranscript] = React.useState<TranscriptEntry[]>([]);
  const [notes, setNotes] = React.useState('');
  const [prepOpen, setPrepOpen] = React.useState(false);
  const [section, setSection] = React.useState<Section>('mock');
  const [progressOpen, setProgressOpen] = React.useState(false);
  const [serverSessionId, setServerSessionId] = React.useState('');
  const serverSessionRef = React.useRef('');
  const finishedRef = React.useRef(false);
  const [finishError, setFinishError] = React.useState('');
  const transcriptRef = React.useRef<TranscriptEntry[]>([]);
  const notesRef = React.useRef('');
  const lastSavedRef = React.useRef('');
  const lastSavedAtRef = React.useRef(0);
  const [rpPhase, setRpPhase] = React.useState<RolePlayPhase>(1);
  const [phaseStartedAt, setPhaseStartedAt] = React.useState(0);
  const [phaseById, setPhaseById] = React.useState<Record<string, RolePlayPhase>>({});
  const rpPhaseRef = React.useRef<RolePlayPhase>(1);
  const phaseByIdRef = React.useRef<Record<string, RolePlayPhase>>({});
  // The AI utterance that announces a phase switch still belongs to the phase it started in.
  const graceRef = React.useRef<RolePlayPhase | null>(null);
  const [liveFailed, setLiveFailed] = React.useState(false);
  const [content, setContent] = React.useState<ContentBundle | null>(null);
  const [contentError, setContentError] = React.useState('');
  const [contentAttempt, setContentAttempt] = React.useState(0);
  const [startedAt, setStartedAt] = React.useState<number | null>(null);
  const [interviewerSpeaking, setInterviewerSpeaking] = React.useState(false);
  const [muted, setMuted] = React.useState(false);
  const [micLevel, setMicLevel] = React.useState(0);
  const roomRef = React.useRef<RealtimeRoom | null>(null);
  const stopMonitorRef = React.useRef<(() => void) | null>(null);
  const endingRef = React.useRef(false);
  const endFallbackRef = React.useRef<number | null>(null);
  const elapsed = useElapsedTime(startedAt, phase === 'active' || phase === 'ending');

  React.useEffect(() => saveSetup(setup), [setup]);

  React.useEffect(() => {
    transcriptRef.current = transcript;
  }, [transcript]);
  React.useEffect(() => {
    notesRef.current = notes;
  }, [notes]);

  const sessionBody = React.useCallback((): SessionTranscriptBody => {
    const entries = transcriptRef.current;
    return {
      transcript:
        setup.scenario === 'roleplay_screening'
          ? tagTranscriptLines(entries, phaseByIdRef.current)
          : toTranscriptLines(entries),
      notes: notesRef.current,
    };
  }, [setup.scenario]);

  const autosave = React.useCallback(() => {
    const id = serverSessionRef.current;
    if (!id) return;
    const body = sessionBody();
    const signature = `${body.transcript.length}:${body.transcript.at(-1)?.text.length ?? 0}:${body.notes.length}`;
    // Re-send unchanged transcripts at least every 50s: the server auto-finishes rooms idle for 10 minutes.
    if (signature === lastSavedRef.current && Date.now() - lastSavedAtRef.current < 50_000) return;
    lastSavedRef.current = signature;
    lastSavedAtRef.current = Date.now();
    void saveSessionTranscript(id, body).catch(() => {
      lastSavedRef.current = '';
    });
  }, [sessionBody]);

  const sendFinish = React.useCallback(async () => {
    const id = serverSessionRef.current;
    if (!id) return;
    const body = sessionBody();
    // Keep a local copy until the server confirms, so it can be resent from Sessions.
    addPending({ sessionId: id, setup, body, savedAt: Date.now() });
    setFinishError('');
    let lastFailure: unknown = null;
    for (const delay of [0, 1_000, 2_500, 6_000]) {
      if (delay) await new Promise((resolve) => window.setTimeout(resolve, delay));
      try {
        await finishSession(id, body);
        removePending(id);
        return;
      } catch (finishFailure: unknown) {
        // 409 means the server already has a finished session.
        if (finishFailure instanceof ApiError && finishFailure.status === 409) {
          removePending(id);
          return;
        }
        lastFailure = finishFailure;
      }
    }
    setFinishError(
      `${formatError(lastFailure)} Your transcript is saved on this device — you can resend it from Sessions & progress.`,
    );
  }, [sessionBody, setup]);

  const closeRoom = React.useCallback(() => {
    if (endFallbackRef.current !== null) window.clearTimeout(endFallbackRef.current);
    endFallbackRef.current = null;
    stopMonitorRef.current?.();
    stopMonitorRef.current = null;
    roomRef.current?.close();
    roomRef.current = null;
    setInterviewerSpeaking(false);
    setMicLevel(0);
    setMuted(false);
  }, []);

  const completeInterview = React.useCallback(() => {
    if (finishedRef.current) return;
    finishedRef.current = true;
    setPhase((current) => (current === 'active' ? 'ending' : current));
    void (async () => {
      // Give pending transcript deltas (in-progress speech) up to ~2 s to land before the snapshot.
      const waitStart = Date.now();
      while (Date.now() - waitStart < 2_000 && transcriptRef.current.some((entry) => entry.status === 'speaking')) {
        await new Promise((resolve) => window.setTimeout(resolve, 100));
      }
      await new Promise((resolve) => window.setTimeout(resolve, 50));
      const finishing = sendFinish();
      closeRoom();
      setPhase('ended');
      endingRef.current = false;
      await finishing;
    })();
  }, [closeRoom, sendFinish]);

  // Autosave to the server every ~20 s, on phase changes, and right before the room closes.
  React.useEffect(() => {
    if (phase !== 'active' && phase !== 'ending') return undefined;
    const timer = window.setInterval(autosave, 20_000);
    return () => window.clearInterval(timer);
  }, [autosave, phase]);
  React.useEffect(() => {
    if (phase === 'active') autosave();
  }, [autosave, phase, rpPhase]);

  React.useEffect(() => {
    let active = true;
    void authStatus()
      .then((authenticated) => {
        if (active) setPhase(authenticated ? 'lobby' : 'locked');
      })
      .catch((statusError: unknown) => {
        if (!active) return;
        setError(formatError(statusError));
        setPhase('locked');
      });
    return () => {
      active = false;
      closeRoom();
    };
  }, [closeRoom]);

  // One minute before the end the AI is told to wrap up; at the end the call is closed.
  React.useEffect(() => {
    if (phase !== 'active' || !startedAt) return undefined;
    const totalMs = setup.durationMinutes * 60_000;
    const remainingMs = Math.max(0, totalMs - (Date.now() - startedAt));
    const nearlyUp = window.setTimeout(() => {
      // Role play phases 1-2 are driven by the phase buttons / auto-advance; only wrap up during Q&A.
      if (setup.scenario !== 'roleplay_screening' || rpPhaseRef.current === 3) {
        roomRef.current?.sendMessage(NEARLY_UP_MESSAGE[setup.scenario]);
      }
    }, Math.max(0, remainingMs - 60_000));
    const hardCap = window.setTimeout(() => {
      endingRef.current = true;
      setPhase('ending');
      roomRef.current?.sendMessage(TIME_UP_MESSAGE);
      endFallbackRef.current = window.setTimeout(completeInterview, 15_000);
    }, remainingMs);
    return () => {
      window.clearTimeout(nearlyUp);
      window.clearTimeout(hardCap);
    };
  }, [completeInterview, phase, setup.durationMinutes, setup.scenario, startedAt]);

  // Ideals content is fetched after auth so it never ships in the public bundle.
  const authed = phase !== 'checking' && phase !== 'locked';
  React.useEffect(() => {
    if (!authed || content) return undefined;
    let active = true;
    setContentError('');
    fetchContent()
      .then((raw) => active && setContent(normalizeContent(raw)))
      .catch((loadError: unknown) => active && setContentError(formatError(loadError)));
    return () => {
      active = false;
    };
  }, [authed, content, contentAttempt]);

  const advanceRoleplay = React.useCallback((next: RolePlayPhase) => {
    if (next <= rpPhaseRef.current) return;
    graceRef.current = rpPhaseRef.current;
    rpPhaseRef.current = next;
    setRpPhase(next);
    setPhaseStartedAt(Date.now());
    roomRef.current?.sendMessage(next === 2 ? START_ROLEPLAY_MESSAGE : END_ROLEPLAY_MESSAGE);
  }, []);

  // Phase 2 auto-ends at its time limit; phase 1 never auto-advances.
  React.useEffect(() => {
    if (phase !== 'active' || setup.scenario !== 'roleplay_screening' || rpPhase !== 2) return undefined;
    const left = Math.max(0, phaseMinutes(setup.durationMinutes)[2] * 60_000 - (Date.now() - phaseStartedAt));
    const timer = window.setTimeout(() => advanceRoleplay(3), left);
    return () => window.clearTimeout(timer);
  }, [advanceRoleplay, phase, phaseStartedAt, rpPhase, setup.durationMinutes, setup.scenario]);

  const handleLogin = async (accessCode: string) => {
    setLoginLoading(true);
    setError('');
    try {
      await login(accessCode);
      setPhase('lobby');
    } catch (loginError: unknown) {
      setError(formatError(loginError));
    } finally {
      setLoginLoading(false);
    }
  };

  const handleLogout = async () => {
    closeRoom();
    await logout().catch(() => undefined);
    setPhase('locked');
    setContent(null);
    setPrepOpen(false);
    setProgressOpen(false);
    setError('');
  };

  const handleStart = async (startSetup: InterviewSetup = setup) => {
    // iOS Safari: audio must be unlocked inside this click, before any await.
    if (isInAppBrowser()) {
      setError(IN_APP_MESSAGE);
      return;
    }
    unlockAudio();
    setError('');
    setLiveFailed(false);
    serverSessionRef.current = '';
    setServerSessionId('');
    setFinishError('');
    finishedRef.current = false;
    lastSavedRef.current = '';
    rpPhaseRef.current = 1;
    graceRef.current = null;
    phaseByIdRef.current = {};
    setRpPhase(1);
    setPhaseById({});
    setTranscript([]);
    setNotes('');
    setPhase('connecting');
    endingRef.current = false;
    try {
      const { monitorMicrophone } = await import('./realtime');
      const callbacks = {
        onAgentEnd: () => {
          if (endingRef.current) completeInterview();
        },
        onError: (realtimeError: Error) => setError(realtimeError.message),
        onHistory: (entries: TranscriptEntry[]) => {
          // Each entry keeps the phase it first appeared in.
          let changed = false;
          for (const entry of entries) {
            if (!phaseByIdRef.current[entry.id]) {
              let assigned = rpPhaseRef.current;
              if (entry.role === 'interviewer' && graceRef.current !== null) {
                assigned = graceRef.current;
                graceRef.current = null;
              }
              phaseByIdRef.current = { ...phaseByIdRef.current, [entry.id]: assigned };
              changed = true;
            }
          }
          if (changed) setPhaseById(phaseByIdRef.current);
          setTranscript(entries);
        },
        onSpeaking: setInterviewerSpeaking,
      };
      let room: RealtimeRoom;
      if (startSetup.voiceEngine === 'gpt-live-1') {
        const { connectLiveRoom } = await import('./live');
        try {
          room = await connectLiveRoom({ ...callbacks, setup: startSetup });
        } catch (liveError: unknown) {
          if (!(liveError instanceof ApiError && liveError.status === 401)) setLiveFailed(true);
          throw liveError;
        }
      } else {
        const { connectRealtimeRoom } = await import('./realtime');
        const credential = await createRealtimeCredential(startSetup);
        room = await connectRealtimeRoom({ ...callbacks, credential });
      }
      roomRef.current = room;
      stopMonitorRef.current = monitorMicrophone(room.mediaStream, setMicLevel);
      void createSession(startSetup)
        .then((created) => {
          serverSessionRef.current = created.id;
          setServerSessionId(created.id);
        })
        .catch(() => undefined);
      const now = Date.now();
      setStartedAt(now);
      setPhaseStartedAt(now);
      setPhase('active');
    } catch (startError: unknown) {
      closeRoom();
      const message = formatError(startError);
      if (startError instanceof ApiError && startError.status === 401) {
        setPhase('locked');
      } else {
        setPhase('lobby');
      }
      setError(message);
    }
  };

  const handleMute = () => {
    const next = !muted;
    roomRef.current?.mute(next);
    setMuted(next);
  };

  // The written debrief comes from /api/debrief, so ending needs no spoken wrap-up.
  const requestEnd = () => {
    if (!roomRef.current) return;
    completeInterview();
  };

  const restart = () => {
    closeRoom();
    setTranscript([]);
    setStartedAt(null);
    setError('');
    setPrepOpen(false);
    setProgressOpen(false);
    setSection('mock');
    setPhase('lobby');
  };

  if (phase === 'checking') {
    return (
      <main className="checking-page" aria-label="Checking access">
        <Brand />
        <div className="checking-line"><span /></div>
      </main>
    );
  }
  if (phase === 'locked') {
    return <AccessGate error={error} loading={loginLoading} onSubmit={handleLogin} />;
  }
  if (phase === 'lobby' && !content) {
    return (
      <main className="checking-page" aria-label="Loading materials">
        <Brand />
        {contentError ? (
          <div className="form-error" role="alert">
            {contentError}{' '}
            <button className="text-button" onClick={() => setContentAttempt((count) => count + 1)} type="button">Retry</button>
          </div>
        ) : (
          <div className="checking-line"><span /></div>
        )}
      </main>
    );
  }
  if (phase === 'lobby') {
    return (
      <ContentContext.Provider value={content}>
        <TopNav active={section} onLogout={() => void handleLogout()} onSelect={setSection} />
        {section === 'prep' && <PrepLibrary setup={setup} />}
        {section === 'sessions' && <Sessions backLabel="" embedded onBack={() => setSection('mock')} />}
        {section === 'mock' && (
          <Lobby
            error={error}
            onPrep={() => setSection('prep')}
            onRetryLive={liveFailed ? () => handleStart() : undefined}
            onStart={() => handleStart()}
            onUseRealtime={
              liveFailed
                ? () => {
                    const next: InterviewSetup = { ...setup, voiceEngine: 'gpt-realtime-2.1' };
                    setSetup(next);
                    void handleStart(next);
                  }
                : undefined
            }
            setSetup={setSetup}
            setup={setup}
          />
        )}
      </ContentContext.Provider>
    );
  }
  if (phase === 'connecting') return <Connecting setup={setup} />;
  if (phase === 'active' || phase === 'ending') {
    return (
      <ContentContext.Provider value={content}>
      <InterviewRoom
        elapsed={elapsed}
        error={error}
        interviewerSpeaking={interviewerSpeaking}
        micLevel={micLevel}
        muted={muted}
        notes={notes}
        onEnd={requestEnd}
        onEndRoleplay={() => advanceRoleplay(3)}
        onStartRoleplay={() => advanceRoleplay(2)}
        phaseById={phaseById}
        phaseStartedAt={phaseStartedAt}
        rpPhase={rpPhase}
        onInterrupt={() => roomRef.current?.interrupt()}
        onMute={handleMute}
        onNotes={setNotes}
        onSendText={(message) => roomRef.current?.sendMessage(message)}
        phase={phase}
        setup={setup}
        transcript={transcript}
      />
      </ContentContext.Provider>
    );
  }
  return (
    <ContentContext.Provider value={content}>
      {prepOpen && <PrepPack backLabel="Back to debrief" onBack={() => setPrepOpen(false)} setup={setup} />}
      {progressOpen && <Sessions backLabel="Back to debrief" onBack={() => setProgressOpen(false)} />}
      <div hidden={prepOpen || progressOpen}>
        <Debrief
          elapsed={elapsed}
          notes={notes}
          onPrep={() => setPrepOpen(true)}
          finishError={finishError}
          onRetryFinish={() => void sendFinish()}
          onSessions={() => setProgressOpen(true)}
          sessionId={serverSessionId}
          phaseById={phaseById}
          onRestart={restart}
          setup={setup}
          transcript={transcript}
        />
      </div>
    </ContentContext.Provider>
  );
}
